import React, { useState, useEffect, useRef } from 'react';
import {
  List,
  Radio,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
} from 'lucide-react';
import {
  getHistoryMeta,
  parseDuration,
  formatTime12,
} from '../utils/audioUtils';

const QueueCarousel = ({
  queue,
  currentId,
  onPlay,
  onRemove,
  onReorder,
  onClear,
  isRadioMode,
  radioState,
  history,
}) => {
  const displayQueue =
    isRadioMode && radioState
      ? radioState.schedule.filter((i) => i.type !== 'countdown')
      : queue;

  const [editMode, setEditMode] = useState(false);
  const longPressTimer = useRef(null);
  const queueRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (queueRef.current && !queueRef.current.contains(e.target)) {
        setEditMode(false);
      }
    };
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(() => {
      setEditMode(true);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 800);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const now = Date.now();
  if (displayQueue.length === 0)
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 flex justify-center items-center text-slate-500 h-40">
        <List size={24} className="mb-2 opacity-50" />
        <span className="text-sm">Queue is empty</span>
      </div>
    );

  return (
    <div
      ref={queueRef}
      className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden flex flex-col h-full"
    >
      <div className="px-4 py-3 border-b border-slate-800 flex justify-between items-center bg-slate-900/50 flex-shrink-0">
        <h3 className="text-slate-100 font-semibold text-sm flex items-center gap-2">
          {isRadioMode ? (
            <>
              <span className="text-red-400 animate-pulse font-bold">
                ● LIVE Schedule
              </span>
            </>
          ) : (
            <>
              <Radio size={16} className="text-blue-500" /> Up Next
            </>
          )}
        </h3>
        {!isRadioMode && (
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(!editMode)}
              className={`text-xs font-medium ${
                editMode ? 'text-red-400' : 'text-slate-400'
              }`}
            >
              {editMode ? 'Done' : 'Edit'}
            </button>
            <button
              onClick={onClear}
              className="text-xs text-red-400 font-medium"
            >
              Clear
            </button>
          </div>
        )}
      </div>
      <div className="p-4 flex gap-3 overflow-x-auto lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:space-y-2 lg:gap-0 custom-scrollbar flex-grow">
        {displayQueue.map((item) => {
          const isActive = isRadioMode
            ? item.uniqueKey === currentId
            : item.id === currentId;
          if (
            isRadioMode &&
            radioState &&
            radioState.blockStartMs + item.playEndOffset * 1000 < now - 60000
          )
            return null;
          let badgeText = null;
          if (isRadioMode && radioState) {
            if (isActive) badgeText = 'ON AIR';
            else
              badgeText = formatTime12(
                new Date(radioState.blockStartMs + item.playStartOffset * 1000)
              );
          }
          const saved = getHistoryMeta(history, item.id).progress;
          const total = parseDuration(item.duration);
          const progressPct = total > 0 ? (saved / total) * 100 : 0;

          return (
            <div
              key={isRadioMode ? item.uniqueKey : item.id}
              title={item.title}
              className={`flex-shrink-0 w-40 lg:w-full p-2 lg:p-3 rounded-lg border transition-all group relative lg:flex lg:flex-col lg:gap-2 lg:h-auto ${
                isActive
                  ? 'bg-slate-800 border-blue-500/50 shadow-lg'
                  : 'bg-slate-800/50 border-slate-800 hover:bg-slate-800 hover:border-slate-700'
              }`}
              onTouchStart={!isRadioMode ? handleTouchStart : undefined}
              onTouchEnd={!isRadioMode ? handleTouchEnd : undefined}
              onMouseDown={!isRadioMode ? handleTouchStart : undefined}
              onMouseUp={!isRadioMode ? handleTouchEnd : undefined}
            >
              <div className="lg:flex lg:gap-3 lg:items-start">
                <div
                  className="aspect-video w-full lg:w-32 lg:h-20 lg:aspect-auto lg:flex-shrink-0 bg-slate-700 rounded mb-2 lg:mb-0 overflow-hidden cursor-pointer relative"
                  onClick={() => !isRadioMode && onPlay(item)}
                >
                  <img
                    src={item.thumbnail}
                    alt={item.title}
                    className={`w-full h-full object-cover opacity-80 transition-all ${
                      editMode ? 'animate-jiggle' : ''
                    }`}
                  />

                  {editMode && !isRadioMode && (
                    <div
                      className="absolute inset-0 bg-black/60 flex items-center justify-center z-30"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(item.id);
                      }}
                    >
                      <Trash2
                        size={32}
                        className="text-red-500 drop-shadow-lg"
                      />
                    </div>
                  )}

                  {isActive && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 animate-pulse" />
                    </div>
                  )}
                  {isRadioMode && badgeText && (
                    <div
                      className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-bold z-10 backdrop-blur-md border ${
                        badgeText === 'ON AIR'
                          ? 'bg-red-600/80 text-white border-red-500'
                          : 'bg-black/60 text-slate-300 border-white/10'
                      }`}
                    >
                      {badgeText}
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                    <div
                      className="h-full bg-red-500"
                      style={{ width: `${Math.min(progressPct, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="hidden lg:flex lg:flex-col lg:gap-1 lg:text-xs lg:text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={12} className="flex-shrink-0" />
                    <span className="truncate">{item.date}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin size={12} className="flex-shrink-0" />
                    <span className="truncate">{item.place}</span>
                  </div>
                </div>

                {!isRadioMode && (
                  <div className="hidden lg:flex lg:flex-col lg:gap-1 lg:ml-auto">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onReorder(item.id, -1);
                        }}
                        className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
                        title="Move Up"
                      >
                        <ChevronLeft size={14} className="rotate-90" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onReorder(item.id, 1);
                        }}
                        className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
                        title="Move Down"
                      >
                        <ChevronRight size={14} className="rotate-90" />
                      </button>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(item.id);
                      }}
                      className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-red-400"
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-col lg:mt-1">
                <h4 className="text-xs font-medium text-slate-200 truncate leading-tight mb-1 lg:mb-0 lg:whitespace-normal lg:line-clamp-2">
                  {item.title}
                </h4>
                <div className="text-[10px] text-slate-500 lg:hidden">
                  {item.duration}
                </div>
              </div>

              {!isRadioMode && (
                <div className="flex items-center justify-between bg-slate-900/50 rounded px-1 py-1 lg:hidden">
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReorder(item.id, -1);
                      }}
                      className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReorder(item.id, 1);
                      }}
                      className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(item.id);
                    }}
                    className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-red-400"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QueueCarousel;
